import type { Step } from "react-joyride";
import {
  DEFAULT_TARGET_WAIT_MS,
  SLOW_TARGET_WAIT_MS,
  TOUR_MODAL_Z_INDEX,
} from "./constants";
import { resolveRouteParams } from "./id-resolvers";
import { buildPath, hasRouteParams } from "./route-matcher";
import { createTargetGetter, resolveTargetElement } from "./target-resolver";
import type {
  ResolvedTourStep,
  TourDefinition,
  TourEvalContext,
  TourStepConfig,
} from "../types/tour.types";

const filterSteps = (
  steps: TourStepConfig[],
  context: TourEvalContext,
): TourStepConfig[] => steps.filter((step) => step.when?.(context) ?? true);

export const isTourEligible = (
  tour: TourDefinition,
  context: TourEvalContext,
): boolean => {
  if (!context.featuresLoaded) return false;
  if (tour.when && !tour.when(context)) return false;
  return filterSteps(tour.steps, context).length > 0;
};

const targetWaitFor = (config: TourStepConfig): number =>
  config.targetWaitTimeout ??
  (config.skipScroll ? SLOW_TARGET_WAIT_MS : DEFAULT_TARGET_WAIT_MS);

export const stepTargetProbe =
  (config: TourStepConfig) => (): HTMLElement | null => {
    if (config.target === "body") return document.body;
    return resolveTargetElement(config.target, config.fallbackTargets);
  };

const toJoyrideStep = (
  config: TourStepConfig,
  context: TourEvalContext,
): Step => {
  const shared = {
    id: config.id,
    title: config.title,
    content:
      typeof config.content === "function"
        ? config.content(context)
        : config.content,
    targetWaitTimeout: targetWaitFor(config),
    spotlightPadding: config.spotlightPadding ?? 8,
    skipScroll: config.skipScroll ?? false,
    blockTargetInteraction: !config.allowTargetInteraction,
    ...(config.aboveModalLayer
      ? { zIndex: TOUR_MODAL_Z_INDEX, disableFocusTrap: true }
      : {}),
    skipBeacon: true,
  };

  // A centred step has nothing to spotlight, so it neither resolves an element
  // nor scrolls to one.
  if (config.target === "body") {
    return {
      ...shared,
      placement: config.placement ?? "center",
      target: "body",
    };
  }

  const getTarget = createTargetGetter(config.target, config.fallbackTargets);

  return {
    ...shared,
    placement: config.placement ?? "bottom",
    target: getTarget,
    scrollTarget: getTarget,
  };
};

export const resolveTour = async (
  tour: TourDefinition,
  context: TourEvalContext,
): Promise<ResolvedTourStep[]> => {
  const configs = filterSteps(tour.steps, context);

  const paramCache = new Map<string, Record<string, string> | null>();
  const resolved: ResolvedTourStep[] = [];

  for (const config of configs) {
    const routePattern = config.route ?? tour.route;

    if (!hasRouteParams(routePattern)) {
      resolved.push({
        config,
        step: toJoyrideStep(config, context),
        route: routePattern,
      });
      continue;
    }

    const cacheKey = `${routePattern}|${JSON.stringify(config.routeParams ?? {})}`;
    if (!paramCache.has(cacheKey)) {
      paramCache.set(cacheKey, await resolveRouteParams(config.routeParams));
    }

    const params = paramCache.get(cacheKey) ?? null;
    if (!params) continue;

    resolved.push({
      config,
      step: toJoyrideStep(config, context),
      route: buildPath(routePattern, params),
    });
  }

  return resolved;
};
